import React, { useEffect } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { Button } from '../ui/button'
import { Archive, ArchiveRestore, Copy, Edit, Eye, FileText, Loader2, MailCheck, Plus, Search, TestTubeDiagonalIcon } from 'lucide-react'
import { ConfirmDialog } from '../ui/confirm-dialog'
import Link from 'next/link'
import { LoadingSpinner } from '../ui/loading-spinner'
import { Badge } from '../ui/badge'
import { TestTemplateModal } from './TestTemplateModal'
import { useCampaignHook } from '@/hooks/useCampaignHook'
import EmptyState from '../EmptyState'

/**
 * TemplateTable
 *
 * Renders a table view of email templates with actions such as:
 * - Preview
 * - Edit
 * - Duplicate
 * - Archive / Restore
 *
 * Handles empty, loading, and filtered states gracefully.
 *
 * Props:
 * @param displayTemplates Array of template objects to display
 * @param loading Loading state for templates
 * @param searchTerm Current search keyword
 * @param selectedCategory Selected category filter
 * @param setSearchTerm Setter for search keyword
 * @param setSelectedCategory Setter for category filter
 * @param setCurrentPage Pagination page setter
 * @param handleDuplicate Function to duplicate a template
 * @param processingIds Set of template IDs currently being processed
 * @param handleArchiveTemplate Function to archive a template
 * @param archivingTemplates Set of template IDs currently archiving
 * @param handleArchiveToggle Function to archive / restore template
 *
 * @returns JSX.Element
 */
const TemplateTable = ({
  displayTemplates,
  loading,
  searchTerm,
  selectedCategory,
  setSearchTerm,
  setSelectedCategory,
  setCurrentPage,
  handleDuplicate,
  processingIds,
  handleArchiveTemplate,
  archivingTemplates,
  handleArchiveToggle
}: any) => {

  /**
   * Formats a date value into a readable string.
   *
   * @param date Date object or ISO string
   * @returns Formatted date string
   */
  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const { testEmail, setTestEmail, sendTestEmail, setFormData, formData } = useCampaignHook()

  return (
    <>
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 transition-colors">

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Template Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Operations</TableHead>
              <TableHead>Test Template</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {/* ================= Empty / Loading State ================= */}
            {displayTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-gray-500">
                  {loading ? (
                    <LoadingSpinner text="Loading..." />
                  ) : searchTerm || selectedCategory !== "all" ? (
                    <div className="w-full flex flex-col items-center justify-center py-10 space-y-4">
                      <EmptyState
                        icon={Search}
                        title="No templates found"
                        description="We couldn't find any templates matching your current search or filter criteria."
                      />

                      <Button
                        variant="outline"
                        onClick={() => {
                          setSearchTerm("")
                          setSelectedCategory("all")
                          setCurrentPage(1)
                        }}
                      >
                        Clear filters
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-10 space-y-4">
                      <EmptyState
                        icon={FileText}
                        title="No templates yet"
                        description="Create your first template to start building and managing your email content."
                      />

                      <Link href="/templates/new">
                        <Button>
                          <Plus className="h-4 w-4 mr-2" />
                          Create Template
                        </Button>
                      </Link>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              /* ================= Template Rows ================= */
              displayTemplates.map((template: any) => (
                <TableRow key={template._id}>
                  <TableCell className="font-medium">
                    <div>{template.name}</div>
                  </TableCell>

                  <TableCell>
                    <Badge variant="outline">{template.type}</Badge>
                  </TableCell>

                  <TableCell>{formatDate(template.createdAt)}</TableCell>

                  <TableCell>
                    <Badge
                      variant={template.isArchived ? "secondary" : "default"}
                      className={
                        template.isArchived
                          ? "bg-gray-100 text-gray-600"
                          : "bg-green-100 text-green-800"
                      }
                    >
                      {template.isArchived ? 'Archived' : 'Active'}
                    </Badge>
                  </TableCell>

                  {/* ================= Actions ================= */}
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {/* Preview */}
                      <Link href={`/templates/${template._id}/preview`}>
                        <Button variant="outline" size="sm" title="Preview">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>

                      {/* Duplicate */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDuplicate(template._id)}
                        disabled={processingIds.has(template._id)}
                        title="Duplicate"
                      >
                        {processingIds.has(template._id) ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>

                      {/* Edit */}
                      <Link href={`/templates/${template._id}/edit`}>
                        <Button variant="outline" size="sm" title="Edit">
                          <Edit className="h-4 w-4" />
                        </Button>
                      </Link>

                      {/* Archive (quick) */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleArchiveTemplate(template._id)}
                        disabled={archivingTemplates.has(template._id)}
                        title="Archive template"
                      >
                        {archivingTemplates.has(template._id) ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Archive className="h-4 w-4" />
                        )}
                      </Button>

                      {/* Archive / Restore with confirmation */}
                      <ConfirmDialog
                        trigger={
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={processingIds.has(template._id)}
                            className={
                              template.isArchived
                                ? "text-green-600 border-green-600"
                                : "text-orange-600 border-orange-600"
                            }
                            title={template.isArchived ? "Restore Template" : "Archive Template"}
                          >
                            {template.isArchived ? (
                              <>
                                <ArchiveRestore className="h-4 w-4 mr-1" />
                                Restore
                              </>
                            ) : (
                              <>
                                <Archive className="h-4 w-4 mr-1" />
                                Archive
                              </>
                            )}
                          </Button>
                        }
                        title={template.isArchived ? "Restore Template" : "Archive Template"}
                        description={
                          template.isArchived
                            ? "Are you sure you want to restore this template? It will become active again."
                            : "Are you sure you want to archive this template? It will be hidden from the main list."
                        }
                        confirmText={template.isArchived ? "Restore" : "Archive"}
                        variant={template.isArchived ? "default" : "destructive"}
                        onConfirm={() =>
                          handleArchiveToggle(template._id, !template.isArchived)
                        }
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <TestTemplateModal testEmail={testEmail} setTestEmail={setTestEmail} sendTestEmail={sendTestEmail} content={template._id} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </>
  )
}

export default TemplateTable
